// src/pages/enterprise/OfficeAddressSearch.js
// PLACES-AUTOCOMPLETE-ADD-ENTERPRISE-FORM-01. The Street field of "Add enterprise" as an
// address search. It calls mgmt-backend's own routes (PL1, routes/placesRoutes.js), so the
// Google key never reaches the browser:
//   GET /api/places/autocomplete?q=&sessionToken=&country=  -> { ok, suggestions: [{ placeId, primaryText, secondaryText }] }
//   GET /api/places/details?placeId=&sessionToken=          -> { ok, place: { street, unit, city, state, postalCode,
//                                                                  countryCode, country, formattedAddress, lat, lng,
//                                                                  timezone, placeId, source, foundAt } }
// One session token per search: made on the first key press, sent with every call, dropped
// once a suggestion is picked (the details call ends Google's billing session).
//
// Any refusal (PLACES_NOT_CONFIGURED, a 502, the staff gate) turns the field into a plain
// text box for the rest of the dialog, with one quiet line and one [PLACES_UI_FALLBACK] log.

import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, TextField, Autocomplete } from '@mui/material';
import axios from 'axios';
import { T6_BASE, authHeader } from './EnterpriseAgentAccessTab';
import { THEME } from '../enterprise-ai/genesisData';

export const MIN_CHARS = 3;
export const DEBOUNCE_MS = 300;

const newSessionToken = () => {
    try { if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID(); } catch (e) { /* fall through */ }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

// The form fields a picked place fills. Google's parts only: a part Google did not return
// stays '' (an empty timezone means the Time Zone call failed; staff pick it, never a guess).
// The unit the person typed is kept when Google has none.
export function officeFromPlace(place, typedUnit) {
    return {
        street: place.street || '',
        unit: place.unit || typedUnit || '',
        city: place.city || '',
        state: place.state || '',
        zip: place.postalCode || '',
        country: place.countryCode || '',
        timezone: place.timezone || '',
    };
}

// What the office on the provision request carries from a picked place. PL1 forwards these
// untouched; org-backend saves them from OFFICE-PLACE-FIELDS-ORG-01 and drops them before that.
export function placeFieldsForOffice(place) {
    if (!place) return {};
    return {
        placeId: place.placeId,
        lat: place.lat,
        lng: place.lng,
        formattedAddress: place.formattedAddress,
        countryCode: place.countryCode,
        source: place.source,
        foundAt: place.foundAt,
    };
}

const reasonOf = (err) => (err && err.response && err.response.data && (err.response.data.code || err.response.data.error))
    || (err && err.response ? 'HTTP_' + err.response.status : 'PANEL_BACKEND_UNREACHABLE');

export default function OfficeAddressSearch({ value, country, onType, onPicked, onPickFailed }) {
    const [suggestions, setSuggestions] = useState([]);
    const [unavailable, setUnavailable] = useState(false);
    const tokenRef = useRef(null);
    const timerRef = useRef(null);
    const seqRef = useRef(0);
    const loggedRef = useRef(false);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const fallBack = (err) => {
        const reason = reasonOf(err);
        if (!loggedRef.current) {
            loggedRef.current = true;
            console.warn('[PLACES_UI_FALLBACK] ' + JSON.stringify({ reason }));
        }
        clearTimeout(timerRef.current);
        setSuggestions([]);
        setUnavailable(true);
    };

    const search = async (q) => {
        const seq = ++seqRef.current;
        const hint = /^[A-Za-z]{2}$/.test(String(country || '').trim()) ? String(country).trim().toUpperCase() : undefined;
        try {
            const headers = await authHeader();
            const res = await axios.get(`${T6_BASE}/api/places/autocomplete`, {
                headers,
                params: { q, sessionToken: tokenRef.current, ...(hint ? { country: hint } : {}) },
            });
            if (seq !== seqRef.current) return;
            setSuggestions(Array.isArray(res.data && res.data.suggestions) ? res.data.suggestions : []);
        } catch (err) {
            if (seq === seqRef.current) fallBack(err);
        }
    };

    const handleInput = (e, v, reason) => {
        if (reason !== 'input') return;
        onType(v);
        if (unavailable) return;
        clearTimeout(timerRef.current);
        if (!v || v.trim().length < MIN_CHARS) {
            seqRef.current++;
            setSuggestions([]);
            return;
        }
        if (!tokenRef.current) tokenRef.current = newSessionToken();
        timerRef.current = setTimeout(() => search(v.trim()), DEBOUNCE_MS);
    };

    const handlePick = async (e, option) => {
        if (!option || typeof option !== 'object') return;
        clearTimeout(timerRef.current);
        seqRef.current++;
        const sessionToken = tokenRef.current || newSessionToken();
        tokenRef.current = null;
        setSuggestions([]);
        try {
            const headers = await authHeader();
            const res = await axios.get(`${T6_BASE}/api/places/details`, {
                headers,
                params: { placeId: option.placeId, sessionToken },
            });
            onPicked(res.data.place);
        } catch (err) {
            fallBack(err);
            if (onPickFailed) onPickFailed(option);
        }
    };

    const inputSx = { color: THEME.textPrimary, bgcolor: THEME.bgDeep, borderRadius: '8px' };

    return (
        <Box>
            <Autocomplete
                freeSolo
                options={unavailable ? [] : suggestions}
                filterOptions={(x) => x}
                getOptionLabel={(o) => (typeof o === 'string' ? o : o.primaryText)}
                isOptionEqualToValue={(o, v) => o.placeId === v.placeId}
                inputValue={value}
                value={null}
                onInputChange={handleInput}
                onChange={handlePick}
                renderOption={(props, o) => (
                    <li {...props} key={o.placeId}>
                        <Box>
                            <Typography sx={{ fontSize: '0.9rem' }}>{o.primaryText}</Typography>
                            {o.secondaryText && (
                                <Typography sx={{ fontSize: '0.75rem', color: THEME.textMuted }}>{o.secondaryText}</Typography>
                            )}
                        </Box>
                    </li>
                )}
                componentsProps={{
                    paper: { sx: { bgcolor: THEME.cardRaised, color: THEME.textPrimary, border: `1px solid ${THEME.borderStrong}` } },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Street *"
                        placeholder={unavailable ? '' : 'Start typing the address'}
                        inputProps={{ ...params.inputProps, autoComplete: 'off', 'data-testid': 'office-street' }}
                        InputLabelProps={{ ...params.InputLabelProps, sx: { color: THEME.textMuted } }}
                        InputProps={{ ...params.InputProps, sx: inputSx }}
                    />
                )}
            />
            {unavailable && (
                <Typography sx={{ color: THEME.textMuted, fontSize: '0.75rem', mt: 0.5 }}>
                    Address search unavailable, enter it by hand
                </Typography>
            )}
        </Box>
    );
}
